import { useEffect, useState } from 'react'
import TenantScreeningServicePlan from './TenantScreeningServicePlanDialog'
import PayTenantBackgroundScreeningDialog from './PayTenantBackgroundScreeningDialog'
import { ScreeningServicePlan, ScreeningServicePlanPayment } from 'common/types/services/api/tenant-screening';
import { useGetOrderPlans } from 'hooks/query/tenant-screening';
import useRequestBackgroundCheck from 'hooks/query/offers/use-request-background-check';
import { openNotificationModal } from 'store/use-notification-modal-store';
import { NotificationType } from 'common/enums/enums';
import { useRequestBackgroundCheckPayment } from 'hooks/query/tenant-screening/useRequestBackgroundCheckPayment';
import { OffersItemDto } from 'common/types/types';
import TenantScreeningConfirmationDialog from './TenantScreeningConfirmationDialog';
import { ScreeningCheckStatus } from 'common/types/services/api/offers/offer-item-dto.type';
import SelectTenantScreeningPayerDialog from './SelectTenantScreeningPayerDialog';
import { PaymentStatuses } from 'common/enums/app/payment-status.enum';
import useCurrentUser from 'hooks/query/user/use-current-user';

interface Props {
  offer?: OffersItemDto;
  onClose: () => void;
  isOpen: boolean;
  location: string;
  setLocation: (location: string) => void;
}

const RequestTenantScreeningDialog = ({ offer, onClose, isOpen, location, setLocation }: Props) => {
  const [servicePlanSelected, setServicePlanSelected] = useState<ScreeningServicePlan | undefined>();
  const [openPayScreeningServiceDialog, setOpenPayScreeningServiceDialog] = useState(false);
  const [openWhoPaysDialog, setOpenWhoPaysDialog] = useState(false);
  const [openTenantScreeningServiceDialog, setOpenTenantScreeningServiceDialog] = useState(false);
  const [alreadyRequestedPlans, setAlreadyRequestedPlans] = useState<ScreeningServicePlan[] | undefined>([]);
  const [paidByScreeningStatus, setPaidByScreeningStatus] = useState<ScreeningCheckStatus>(ScreeningCheckStatus.RequestedLandlord);
  const [screeningPaymentContext, setScreeningPaymentContext] = useState<ScreeningServicePlanPayment | undefined>();
  const { data: user } = useCurrentUser();
  const { data: orderPlans, isPending: orderPlansLoading } = useGetOrderPlans({ location: location, tenantId: offer?.renterId || '', rentApplicationId: offer?.id || ''}, { enabled: !!location && !!offer?.renterId });

  useEffect(() => {
    const requestedPlans = orderPlans?.filter(x => x.isRequested);
    setAlreadyRequestedPlans(requestedPlans);
    if (isOpen && !requestedPlans?.length) {
      setOpenTenantScreeningServiceDialog(true);
    }
  }, [orderPlans]);

  useEffect(() => {
    if (servicePlanSelected) {
      requestBgCheckPayment({
        orderPlanId: servicePlanSelected.id,
        amount: servicePlanSelected.price,
        tenantId: offer?.renterId ?? '',
        rentApplicationId: offer?.id ?? '',
        requestorId: user?.id ?? '',
        isScreeningAccepted: offer?.isAllRunScreeningAccepted
      });
    }
  }, [servicePlanSelected]);
  
  const { mutate: requestBackgroundCheck } = useRequestBackgroundCheck({
    onError: (e) => {
      openNotificationModal({
        type: NotificationType.ERROR,
        title: e?.response?.data?.detail ?? 'Background check cannot be requested, try again later.',
      });
    },
  });

  const { mutateAsync: requestBgCheckPayment, isPending: pendingBgCheckPayment } = useRequestBackgroundCheckPayment({
    onSuccess: data => handlePaymentContextLoaded(data),
    onError: (e) => {
      openNotificationModal({
        type: NotificationType.ERROR,
        title: 'Failed to process background check payment. Try again later.',
      });
    }
  });

  const handlePaymentContextLoaded = (context: ScreeningServicePlanPayment) => {
    if(context) {
      setScreeningPaymentContext(context);
    } 
    
    if (!openWhoPaysDialog) {
       servicePlanSelected?.isPaid || isPaymentMethodSelected(context) ? handleRequestBackgroundCheck() : setOpenWhoPaysDialog(!openWhoPaysDialog);
    }     
  }

  const handleCloseScreeningServicePlanDialog = () => {
    setOpenTenantScreeningServiceDialog(false);
    
    onClose();
    setLocation('');
  }

  const isPaymentMethodSelected = (context: ScreeningServicePlanPayment) => context?.paymentKey && context?.status === PaymentStatuses.Processing;

  const handleSelectScreeningServicePlan = (plan: ScreeningServicePlan, idx) => {
    setServicePlanSelected({ ...plan });
    if (isOpen) {
      onClose();
      setOpenTenantScreeningServiceDialog(false);
    }
  }

  const handleSelectedPayer = () => {
    if (openWhoPaysDialog) {
      setOpenWhoPaysDialog(false);
    }

    if(!openPayScreeningServiceDialog) {
      if (paidByScreeningStatus === ScreeningCheckStatus.RequestedLandlord) {
        servicePlanSelected?.isPaid ? handleRequestBackgroundCheck() : setOpenPayScreeningServiceDialog(true);
      } else if (paidByScreeningStatus === ScreeningCheckStatus.RequestedRenter) {
        handleRequestBackgroundCheck();
      }
    }
  }

  const handleScreeningPaymentSucceeded = () => {
    handleClosePayScreeningServicePlanDialog();

    if(offer?.id && servicePlanSelected) {
      handleRequestBackgroundCheck();
    }
  }

  const handleRequestBackgroundCheck = () => {
    requestBackgroundCheck({
      offerId: offer?.id || '',
      orderPlanId: servicePlanSelected?.id || '',
      userId: offer?.renterId || '',
      location: location,
      screeningCheckStatus: paidByScreeningStatus });
  };

  const handleClosePayScreeningServicePlanDialog = () => {
    if(openPayScreeningServiceDialog) {
      setOpenPayScreeningServiceDialog(!openPayScreeningServiceDialog);
    }
    handleCloseScreeningServicePlanDialog();
  }

  const handleCloseWhoPaysDialog = () => {
    if(openWhoPaysDialog) {
      setOpenWhoPaysDialog(!openWhoPaysDialog);
    }
    handleCloseScreeningServicePlanDialog();
  }

  return (
    <>
      <TenantScreeningConfirmationDialog
        isOpen={isOpen && !!alreadyRequestedPlans?.length}
        plans={alreadyRequestedPlans}
        handleClose={handleCloseScreeningServicePlanDialog}
        handleSubmit={() => setOpenTenantScreeningServiceDialog(true)} 
      />
      <TenantScreeningServicePlan 
        title="Background Check Service Plan"
        isOpen={openTenantScreeningServiceDialog}
        isLoading={orderPlansLoading} 
        data={orderPlans}
        onClose={handleCloseScreeningServicePlanDialog}
        onSelect={handleSelectScreeningServicePlan}
      />
      {openPayScreeningServiceDialog && <PayTenantBackgroundScreeningDialog 
        title="Pay for Background Check"
        isLoading={pendingBgCheckPayment}
        isOpen={openPayScreeningServiceDialog} 
        data={screeningPaymentContext}
        useCapture={!offer?.isAllRunScreeningAccepted}
        onSuccess={handleScreeningPaymentSucceeded}
        onClose={handleClosePayScreeningServicePlanDialog}
      />}
      {openWhoPaysDialog && <SelectTenantScreeningPayerDialog
        isOpen={openWhoPaysDialog}
        onAccept={handleSelectedPayer}
        onSelect={setPaidByScreeningStatus}
        paidByScreeningStatus={paidByScreeningStatus}
        onClose={handleCloseWhoPaysDialog}
        totalPrice={servicePlanSelected && Number((servicePlanSelected.price * (offer?.adultOccupants || 1)).toFixed(2)) || 0}
      />}
    </>
  )
}

export default RequestTenantScreeningDialog
